import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import {
  getCompanyDomain,
  getMemberCompanyVerification,
  postMemberCompanyVerification,
} from "../../../services/memberServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import { FindIdInfoIcon } from "../../Auth/FindId";
import CommonInput from "../../../components/Input/CommonInput";
import CommonButton from "../../../components/Button";
import CertEmailPopup from "../../../components/Popup/CertEmail";
import BottomButtonGroup from "../../../components/Button/Bottom";
import CommonPopup from "../../../components/Popup";
import SimplePopup from "../../../components/Popup/Simple";
import Select from "../../../components/Select";
import { AuthHeader } from "../../../components/Layout/Auth/Header";

import { MyProfileKakaoIcon } from "..";

type Props = {};

export default function MyInfoCertificationCompany(props: Props) {
  const navigate = useNavigate();
  const [popup, setPopup] = useState<
    "certEmail" | "certFailed" | "errorPopup" | "completeCert" | ""
  >("");
  const [authCode, setAuthCode] = useState<string>("");
  const [isRequested, setIsRequested] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [optionList, setOptionList] = useState<string[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [id, setId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [errorMessage, setErrorMessage] = useState<string>("");

  const validateBeforeRequest = () => {
    if (!selectedDomain) {
      setPopup("errorPopup");
      setError("도메인을 선택해주세요.");
      return false;
    }

    if (!id) {
      setPopup("errorPopup");
      setError("아이디를 입력해주세요.");
      return false;
    }

    if (id.length > 64) {
      setPopup("errorPopup");
      setError("아이디가 너무 깁니다.\n64자 이하로 입력해주세요.");
      return false;
    }

    if (!/[a-zA-Z0-9+-_.]+/.test(id)) {
      setPopup("errorPopup");
      setError("부적절한 아이디 형식입니다.");
      return false;
    }

    return true;
  };

  const validate = () => {
    if (!validateBeforeRequest()) return false;

    if (!authCode || authCode.length < 6) {
      setPopup("errorPopup");
      setError("인증번호를 입력해주세요.");
      return false;
    }

    if (!isRequested) {
      setPopup("errorPopup");
      setError("인증번호를 요청해주세요.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    setSelectedDomain(optionList[0]);
  }, [optionList]);

  useEffect(() => {
    getCompanyDomain().then((res) => {
      setOptionList(res.data.domains);
    });
  }, []);

  return (
    <>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="회사 인증"
          onBackClick={() => navigate("/myinfo")}
        />

        <AuthHeader ruleClassName="border-black">
          <div className="flex flex-col gap-2.5">
            <div>
              <strong>회사 인증</strong>을<br />
              도와드릴게요
            </div>
            <div className="flex flex-col gap-1 text-brand-neutral-50 font-normal">
              <div className="text-base">
                회사 서비스 이용을 위해서 인증이 필요해요
              </div>
              <div className="text-xs">
                * 인증 진행시 회사 이메일 수집에 동의하는 것으로 간주합니다.
              </div>
            </div>
          </div>
        </AuthHeader>

        <div className="flex flex-col">
          <div className="py-5 flex flex-col gap-[5px]">
            <div className="label-grey-medium">현재 아이디(이메일)</div>
            <div className="flex flex-col">
              <div className="text-lg font-medium flex min-h-[50px] gap-1 flex-wrap">
                <div className="flex items-start gap-0.5">
                  <CommonInput
                    value={id}
                    onChange={(text) => {
                      setId(text);
                      setIsRequested(false);
                      setErrorMessage("");
                    }}
                    className="max-w-[120px]"
                  />
                  <span className="h-[50px] flex items-center">@</span>
                  <Select
                    wrapperClassName="w-fit"
                    className="min-w-[135px] w-fit h-[50px] rounded-[3px] px-2.5"
                    value={selectedDomain}
                    optionList={optionList
                      .filter((item) => item?.length && item.length > 0)
                      .map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    onChange={(value) => {
                      setSelectedDomain(value.value);
                      setIsRequested(false);
                    }}
                    defaultValue="도메인 없음"
                    optionOrder="between"
                    unUsePadding
                    onClick={() => {
                      if (
                        optionList
                          .filter((item) => item?.length && item.length > 0)
                          .map((item) => ({
                            label: item,
                            value: item,
                          })).length <= 1
                      ) {
                        setErrorMessage("선택할 수 있는 옵션이 없습니다.");
                      }
                    }}
                    useError={false}
                  />
                </div>
                <div className="h-[50px] flex items-center">
                  <CommonButton
                    variant="tertiary-s"
                    onClick={async () => {
                      if (isRequested) {
                        setAuthCode("");
                      }

                      setErrorMessage("");
                      if (!validateBeforeRequest()) return;

                      setIsLoading(true);
                      try {
                        await postMemberCompanyVerification({
                          email: `${id}@${selectedDomain}`,
                        });
                        setIsRequested(true);
                        setPopup("certEmail");
                      } catch (e: any) {
                        if (e?.response?.data?.error) {
                          setError(e.response.data.error?.replace(". ", ".\n"));
                          setPopup("errorPopup");
                        } else {
                          setError("이메일 인증요청에 실패했습니다.");
                          setPopup("errorPopup");
                        }
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    className="whitespace-nowrap !h-[50px]"
                  >
                    {isRequested ? "다시 보내기" : "인증요청"}
                  </CommonButton>
                </div>
              </div>
              {errorMessage && (
                <div className="text-brand-primary-magenta text-xs px-4 pt-1">
                  *{errorMessage}
                </div>
              )}
            </div>
            {optionList.length === 0 && (
              <div className="text-brand-primary-magenta text-xs px-4 pt-1">
                *회사에서 설정한 도메인이 없습니다.
              </div>
            )}
          </div>

          <div className="py-5 flex flex-col gap-[5px]">
            <div className="py-2.5 text-sm text-brand-neutral-60">인증번호</div>
            <div className={clsx(["relative"])}>
              <CommonInput
                placeholder="인증번호 입력"
                value={authCode}
                onChange={(text) => setAuthCode(text.slice(0, 6))}
                type="number"
                pattern="[0-9]*"
              />
            </div>
          </div>
          <div className="py-5 flex flex-col gap-2.5 bg-[#f7f7f7] items-center">
            <div className="whitespace-pre-line text-center text-brand-neutral-80 text-sm">
              {"이메일 찾기에 어려움이 있다면,\n카카오톡으로 문의해 주세요."}
            </div>
            <a href="https://pf.kakao.com/_xhJxkZK/chat">
              <CommonButton
                variant="transparent"
                className="!bg-[#fee500] w-fit px-4 py-3 text-sm text-black font-medium"
              >
                <MyProfileKakaoIcon />
                카카오톡 문의
              </CommonButton>
            </a>
          </div>
        </div>

        <BottomButtonGroup
          title="인증하기"
          onClick={async () => {
            if (!validate()) return;

            try {
              await getMemberCompanyVerification({
                authCode,
                email: `${id}@${selectedDomain}`,
              });
              setPopup("completeCert");
              navigate("/myinfo");
            } catch (e: any) {
              setPopup("certFailed");
            }
          }}
        />

        <CertEmailPopup popup={popup} onClose={() => setPopup("")} />
        <CommonPopup
          isOpen={popup === "certFailed"}
          title="인증 실패"
          unUseHorizonRule
          padding={0}
          onClose={() => setPopup("")}
        >
          <div className="px-5 pb-5">
            <div className="py-2.5 flex flex-col gap-2.5 items-center">
              <FindIdInfoIcon />
              <div className=" whitespace-pre-line text-center text-brand-neutral-60 text-sm">
                {"인증번호가 다른 것 같아요."}
              </div>
            </div>
          </div>
          <CommonButton
            className="h-bottom-button w-full rounded-none"
            onClick={() => setPopup("")}
          >
            확인
          </CommonButton>
        </CommonPopup>
        <SimplePopup
          isOpen={popup === "errorPopup"}
          message={error}
          onClose={() => setPopup("")}
        />
        <SimplePopup
          isOpen={popup === "completeCert"}
          message="인증이 완료되었습니다."
          onClose={() => setPopup("")}
        />
      </div>
    </>
  );
}
